import React from 'react';
import { ShieldCheck, AlertTriangle, Thermometer, Package, Droplets, AlertCircle } from 'lucide-react';

/**
 * Preview Food Safety Page
 * Public content - no blur needed
 */

const SAFETY_SECTIONS = [
  {
    id: 'raw-food-risks',
    title: 'Raw Food Risks',
    icon: AlertCircle,
    color: 'red',
    summary: 'Understanding the risks of raw or undercooked foods for immunocompromised individuals.'
  },
  {
    id: 'pre-cut-foods',
    title: 'Pre-Cut Foods',
    icon: Package,
    color: 'amber',
    summary: 'Information about pre-cut produce and safer alternatives.'
  },
  {
    id: 'temperature-safety',
    title: 'Temperature Safety',
    icon: Thermometer,
    color: 'orange',
    summary: 'Safe cooking and storage temperatures for food.'
  },
  {
    id: 'storage-hygiene',
    title: 'Storage & Hygiene',
    icon: Droplets,
    color: 'blue',
    summary: 'Proper food storage and kitchen hygiene practices.'
  },
  {
    id: 'immunocompromised',
    title: 'Immunocompromised Considerations',
    icon: ShieldCheck,
    color: 'teal',
    summary: 'Special considerations for people with weakened immune systems.'
  }
];

const getColorClasses = (color) => {
  const colors = {
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
    orange: { bg: 'bg-orange-50', icon: 'text-orange-600', border: 'border-orange-200' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
    teal: { bg: 'bg-teal-50', icon: 'text-teal-600', border: 'border-teal-200' }
  };
  return colors[color] || colors.blue;
};

const PreviewFoodSafetyPage = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
            Food Safety
          </h1>
        </div>
        <p className="text-lg leading-relaxed text-slate-700">
          Important food safety information for people with CLL
        </p>
      </div>

      {/* Introduction */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          Why food safety matters
        </h2>
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            Food safety is important for everyone, but it can be especially relevant for 
            people living with CLL or undergoing treatment. Understanding basic food safety 
            principles can help you make informed choices about what and how you eat.
          </p>
          <p>
            The information here covers general food safety principles. Your healthcare team 
            can advise on what level of caution is appropriate for your individual situation.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Individual Guidance</h3>
            <p className="text-amber-800 leading-relaxed">
              Food safety recommendations may vary based on your immune status and treatment. 
              Discuss specific precautions with your healthcare team.
            </p>
          </div>
        </div>
      </div>

      {/* Safety Sections Overview */}
      <div className="space-y-4">
        {SAFETY_SECTIONS.map((section) => {
          const Icon = section.icon;
          const colors = getColorClasses(section.color);
          
          return (
            <div key={section.id} className={`bg-white rounded-2xl border ${colors.border} p-6`}>
              <div className="flex items-start gap-4">
                <div className={`p-3 ${colors.bg} rounded-xl`}>
                  <Icon className={`h-6 w-6 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {section.summary}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Key Takeaways */}
      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
        <h3 className="text-xl font-semibold text-slate-900 mb-4">Key Takeaways</h3>
        <ul className="space-y-3 text-slate-700">
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Cook foods to safe temperatures, especially meat and eggs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Wash hands and surfaces frequently</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Refrigerate food promptly and check temperatures</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Be cautious with raw or undercooked foods</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-500 mt-1">•</span>
            <span>Discuss specific precautions with your healthcare team</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PreviewFoodSafetyPage;
